import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, Layers, Sigma, Smartphone } from 'lucide-react';
import { LOGOS, FMT_BADGE, logoForValue, fmtDim } from './lib';

// ─── Logolar ─────────────────────────────────────────────────────────────────
export function Logo({ name, sourceKey, className = 'w-4 h-4' }) {
  if (name && LOGOS[name]) return <img src={LOGOS[name]} alt="" className={`${className} object-contain shrink-0`} />;
  if (sourceKey === 'ads_all') return <Layers className={`${className} text-blue-600 shrink-0`} />;
  if (sourceKey === 'adjust') return <Smartphone className={`${className} text-gray-500 shrink-0`} />;
  if (name === 'calc') return <Sigma className={`${className} text-violet-600 shrink-0`} />;
  return null;
}

// Tablo/pivot hücresi: platform ve kaynak değerlerinin yanında logo.
export function DimValue({ dimKey, value }) {
  const logo = logoForValue(dimKey, value);
  const text = fmtDim(value, dimKey);
  if (!logo) return <span className="truncate" title={String(value)}>{text}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full" title={String(value)}>
      <Logo name={logo} className="w-3.5 h-3.5" />
      <span className="truncate">{text}</span>
    </span>
  );
}

export function FmtBadge({ fmt }) {
  return <span className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded bg-gray-100 text-[10px] font-medium text-gray-500">{FMT_BADGE[fmt] || '#'}</span>;
}

// ─── Aramalı seçici ──────────────────────────────────────────────────────────
// groups: [{ key, label, logo, sourceKey, items: [{ value, label, fmt, disabled, hint }] }]
export function FieldPicker({ groups, value, multiple, onChange, renderButton, placeholder = 'Select', emptyLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const selected = useMemo(() => new Set(multiple ? value || [] : value ? [value] : []), [value, multiple]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  useEffect(() => { if (!open) setQ(''); }, [open]);

  const needle = q.trim().toLocaleLowerCase('en');
  const filtered = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => !needle || it.label.toLocaleLowerCase('en').includes(needle) || g.label.toLocaleLowerCase('en').includes(needle)) }))
    .filter((g) => g.items.length);

  const pick = (it) => {
    if (it.disabled) return;
    if (multiple) {
      const next = selected.has(it.value) ? [...selected].filter((v) => v !== it.value) : [...selected, it.value];
      onChange(next);
    } else {
      onChange(it.value);
      setOpen(false);
    }
  };

  const current = !multiple && groups.flatMap((g) => g.items.map((it) => ({ ...it, group: g }))).find((it) => it.value === value);

  return (
    <div className="relative" ref={ref}>
      {renderButton ? renderButton({ open, toggle: () => setOpen((o) => !o), current }) : (
        <button type="button" onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-left ${open ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-300 hover:border-gray-400'}`}>
          {current ? (
            <>
              <Logo name={current.logo || current.group.logo} sourceKey={current.group.sourceKey} />
              <span className="flex-1 truncate text-gray-900">{current.label}</span>
              {current.fmt && <FmtBadge fmt={current.fmt} />}
            </>
          ) : <span className="flex-1 truncate text-gray-400">{value === '' && emptyLabel ? emptyLabel : placeholder}</span>}
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      )}
      {open && (
        <div className="absolute z-[60] left-0 right-0 mt-1 rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="w-4 h-4 text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 text-sm outline-none bg-transparent" />
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {emptyLabel && !multiple && (
              <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50">
                <span className="w-4">{value === '' && <Check className="w-4 h-4 text-blue-600" />}</span>{emptyLabel}
              </button>
            )}
            {filtered.map((g) => (
              <div key={g.key} className="py-1">
                <div className="flex items-center gap-1.5 px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  <Logo name={g.logo} sourceKey={g.sourceKey} className="w-3.5 h-3.5" />{g.label}
                </div>
                {g.items.map((it) => (
                  <button key={it.value} type="button" onClick={() => pick(it)} disabled={it.disabled} title={it.hint || ''}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${it.disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-800 hover:bg-blue-50/60'}`}>
                    <span className={`w-4 h-4 flex items-center justify-center rounded ${multiple ? `border ${selected.has(it.value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}` : ''}`}>
                      {selected.has(it.value) && <Check className={`w-3.5 h-3.5 ${multiple ? 'text-white' : 'text-blue-600'}`} />}
                    </span>
                    {it.logo && <Logo name={it.logo} className="w-3.5 h-3.5" />}
                    <span className="flex-1 truncate">{it.label}</span>
                    {it.hint && <span className="text-[10px] text-gray-400 truncate max-w-[40%]">{it.hint}</span>}
                    {it.fmt && <FmtBadge fmt={it.fmt} />}
                  </button>
                ))}
              </div>
            ))}
            {!filtered.length && <div className="px-3 py-6 text-center text-sm text-gray-400">No results</div>}
          </div>
          {multiple && (
            <div className="flex justify-end px-3 py-2 border-t border-gray-100">
              <button type="button" onClick={() => setOpen(false)} className="text-xs font-medium text-blue-700 hover:text-blue-900">Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
